import dayjs, { Dayjs } from "dayjs";
import { EventDto, EventPartialDto } from "../types/eventTypes";

/**
 * Checks if the registration for an event is open based on the current date and the registration from and to dates of the event
 * @param event The event to check the registration for
 * @returns A boolean indicating whether the registration for the event is open or not
 */
export const checkIfEventRegistrationIsOpen = (event: EventDto | EventPartialDto | undefined): boolean => {
  if (!event) {
    return false;
  }
  const registrationFrom = dayjs(event.registrationFrom);
  const registrationTo = dayjs(event.registrationTo);
  const registrationBeginn = dayjs(event.eventBeginn);
  const now = dayjs();

  // Check if the event is in the past
  if (registrationBeginn.isBefore(now)) {
    return false;
  }

  // If the event is a working weekend, the registration is open until the event begins
  if (event.type === "WW") {
    return true;
  }

  // If only the registration from date is set and it's in the past, the registration is open
  if (!event.registrationTo && event.registrationFrom && registrationFrom.isBefore(now)) {
    return true;
  }

  // If only the registration to date is set and it's in the future, the registration is open
  if (!event.registrationFrom && event.registrationTo && registrationTo.isAfter(now)) {
    return true;
  }

  // If both registration from and to dates are set, check if the current date is between them
  if (event.registrationFrom && event.registrationTo) {
    return registrationFrom.isBefore(now) && registrationTo.isAfter(now);
  }

  // If neither registration from nor to dates are set, the registration is closed
  if (!event.registrationFrom && !event.registrationTo) {
    return false;
  }

  // Default to false
  return false;
};

/**
 * Function to check if a user is signed up for a specific event
 * @param event The event to check the sign up status for
 * @param userId The ID of the user to check the sign up status for
 * @returns A boolean indicating whether the user is signed up for the event or not
 */
export const checkIfUserIsSignedUpForEvent = (event: EventDto | undefined, userId: number | null): boolean => {
  if (!event || !userId) {
    return false;
  }

  // If the event is a working weekend, check if the user is in the wwMembers list
  if (event.type === "WW") {
    return event.wwMembers.some((member) => member.memberId === userId);
  }

  // For other event types, check if the user is in the members or organizers list
  return (
    event.members.some((member) => member.memberId === userId) ||
    event.organizers.some((organizer) => organizer.memberId === userId)
  );
};

/**
 * Applies the base filters for the events overview page, which are used for both the all events and my events tabs, to a list of events
 * @param events The events to apply the filters to
 * @param displayPastEvents Whether to display past events or not
 * @param startMonth The start month to display events from, if null or undefined, all events from the past are displayed
 * @param endMonth The end month to display events until, if null or undefined, all events from the future are displayed
 * @param eventTypeFilter The event types to filter for, if empty, all event types are displayed
 * @returns The filtered events based on the provided filters
 */
export const applyFiltersAndSortEvents = (
  events: EventPartialDto[],
  displayPastEvents: boolean,
  startMonth: Dayjs | null,
  endMonth: Dayjs | null,
  eventTypeFilter: string[],
  nameFilter: string
) => {
  const today = dayjs().startOf("d");
  const yesterday = dayjs().subtract(1, "day").endOf("d");
  const filteredEvents = events
    // filters the events based on whether they are in the past or not
    .filter((event) => {
      const end = event.eventEnd ? dayjs(event.eventEnd) : dayjs(event.eventBeginn);

      if (displayPastEvents) {
        return end.isBefore(today);
      } else {
        return end.isAfter(yesterday);
      }
    })
    // filters the events based on the start month filter (inclusive)
    .filter((event) => (startMonth ? !dayjs(event.eventBeginn).isBefore(startMonth) : true))
    // filters the events based on the end month filter (inclusive)
    .filter((event) => (endMonth ? !dayjs(event.eventBeginn).isAfter(endMonth) : true))
    // filters the events based on their type
    .filter((event) => (eventTypeFilter.length > 0 ? eventTypeFilter.includes(event.type) : true))
    // filters the events based on their name
    .filter((event) => (nameFilter ? event.eventName.toLowerCase().includes(nameFilter.toLowerCase()) : true));

  // sorts the events by date
  const sortedEvents = filteredEvents.sort((a, b) => dayjs(a.eventBeginn).valueOf() - dayjs(b.eventBeginn).valueOf());

  // If past events are displayed, reverse the order to show the most recent past events first
  if (displayPastEvents) {
    sortedEvents.reverse();
  }
  return sortedEvents;
};
